import React, { Suspense } from 'react';
import { lazyPage, ChunkErrorBoundary, PageFallback } from './utils/lazyPage';
import { Routes, Route, Navigate } from 'react-router-dom';

// Auth components
import Login from './components/auth/Login';
import Register from './components/auth/Register';
import ForgotPassword from './components/auth/ForgotPassword';
import ResetPassword from './components/auth/ResetPassword';
import VerifyEmail from './components/auth/VerifyEmail';

// Layout
import Layout from './components/common/Layout';
import ProtectedRoute from './components/common/ProtectedRoute';

// Pages
const DashboardPage = lazyPage(() => import('./components/dashboard/DashboardPage'));
const TransactionsPage = lazyPage(() => import('./components/transactions/TransactionsPage'));
const ReconciliationPage = lazyPage(() => import('./components/reconciliation/ReconciliationPage'));
const NeedsYouPage = lazyPage(() => import('./components/needsYou/NeedsYouPage'));
const TradesPage = lazyPage(() => import('./components/trades/TradesPage'));
const RecodePage = lazyPage(() => import('./components/recode/RecodePage'));
const NAVPage = lazyPage(() => import('./components/nav/NAVPage'));
const TaxPage = lazyPage(() => import('./components/tax/TaxPage'));
const TradingPlanDocumentPage = lazyPage(() => import('./components/tradingPlan/TradingPlanDocumentPage'));
const SettingsPage = lazyPage(() => import('./components/settings/SettingsPage'));
const HelpPage = lazyPage(() => import('./components/help/HelpPage'));
const CoachPage = lazyPage(() => import('./components/chat/CoachPage'));
const FeedbackPage = lazyPage(() => import('./components/feedback/FeedbackPage'));

// Mind & Markets
const JournalListPage = lazyPage(() => import('./components/mindMarkets/JournalListPage'));
const JournalFormPage = lazyPage(() => import('./components/mindMarkets/JournalFormPage'));
const PsychologyPage = lazyPage(() => import('./components/mindMarkets/PsychologyPage'));
const PsychologyDailyForm = lazyPage(() => import('./components/mindMarkets/PsychologyDailyForm'));
const PsychologyWeeklyForm = lazyPage(() => import('./components/mindMarkets/PsychologyWeeklyForm'));
const PsychologyMonthlyForm = lazyPage(() => import('./components/mindMarkets/PsychologyMonthlyForm'));

// Charts
const MacroPage = lazyPage(() => import('./components/macro/MacroPage'));
const NotesPage = lazyPage(() => import('./components/notes/NotesPage'));

// Onboarding
const OnboardingWizard = lazyPage(() => import('./components/onboarding/OnboardingWizard'));

// Escalation resume page — fix for Day 28 27.F gap. Backend POST
// /api/v1/escalations/resume + email link have existed since 27.E/G,
// but no frontend route consumed them until this page landed.
const EscalationResumePage = lazyPage(() => import('./components/transactions/import/EscalationResumePage'));

// Code splitting (24 Sep 2026 speed audit). The app shipped as ONE 2.9 MB
// script that every visit had to download and parse before the login page
// could draw. Each page below is now its own chunk, fetched the first time it
// is opened; the login/register pages and the app shell stay in the main
// bundle. Behaviour is unchanged — a chunk is the same code, loaded later.
// A page's chunk loads INSIDE the app shell: the Suspense boundary sits around
// <Outlet /> in Layout, so the sidebar and header stay mounted (and their
// effects do not re-run) while a page downloads. The two protected routes
// that live outside the shell get their own boundary here. A boundary
// around <Routes> itself would unmount the whole shell on every first visit
// to a page, which is both a flash and a source of re-run effects.
const Lazy = ({ children }) => (
  <ChunkErrorBoundary>
    <Suspense fallback={<PageFallback />}>{children}</Suspense>
  </ChunkErrorBoundary>
);

const AppRoutes = () => {
  return (
    <Routes>
      {/* Public routes */}
      <Route path="/login" element={<Login />} />
      <Route path="/register" element={<Register />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/verify-email" element={<VerifyEmail />} />

      {/* Onboarding (protected, but outside Layout shell) */}
      <Route
        path="/onboarding"
        element={
          <ProtectedRoute skipOnboardingCheck>
            <Lazy><OnboardingWizard /></Lazy>
          </ProtectedRoute>
        }
      />

      {/* Popped-out Coach (protected, outside the Layout shell — its own window) */}
      <Route
        path="/coach"
        element={
          <ProtectedRoute skipOnboardingCheck>
            <Lazy><CoachPage /></Lazy>
          </ProtectedRoute>
        }
      />

      {/* Protected routes */}
      <Route
        path="/"
        element={
          <ProtectedRoute>
            <Layout />
          </ProtectedRoute>
        }
      >
        <Route index element={<Navigate to="/dashboard" replace />} />
        <Route path="dashboard" element={<DashboardPage />} />
        <Route path="transactions" element={<TransactionsPage />} />
        {/* Resume-from-email landing page. JWT in ?token=… is the
            backend auth; ProtectedRoute around this <Layout> means
            logged-out clicks get bounced through /login and back. */}
        <Route path="escalations/:id/resume" element={<EscalationResumePage />} />
        <Route path="reconciliation" element={<ReconciliationPage />} />
        <Route path="needs-you" element={<NeedsYouPage />} />
        <Route path="trades" element={<TradesPage />} />
        <Route path="recode" element={<RecodePage />} />
        <Route path="nav" element={<NAVPage />} />
        <Route path="tax" element={<TaxPage />} />
        {/* The plan is one document (1 Oct 2026); /:id opens a version from History for reading. */}
        <Route path="trading-plan" element={<TradingPlanDocumentPage />} />
        <Route path="trading-plan/:id" element={<TradingPlanDocumentPage />} />
        <Route path="macro" element={<MacroPage />} />
        <Route path="notes" element={<NotesPage />} />
        <Route path="notes/:noteId" element={<NotesPage />} />
        <Route path="charts" element={<Navigate to="/macro" replace />} />
        <Route path="feedback" element={<FeedbackPage />} />
        <Route path="mind-markets/journal" element={<JournalListPage />} />
        <Route path="mind-markets/journal/create" element={<JournalFormPage />} />
        <Route path="mind-markets/journal/edit/:id" element={<JournalFormPage />} />
        <Route path="mind-markets/psychology" element={<PsychologyPage />} />
        <Route path="mind-markets/psychology/daily/:date" element={<PsychologyDailyForm />} />
        <Route path="mind-markets/psychology/weekly/:date" element={<PsychologyWeeklyForm />} />
        <Route path="mind-markets/psychology/monthly/:date" element={<PsychologyMonthlyForm />} />
        <Route path="settings" element={<SettingsPage />} />
        <Route path="help" element={<HelpPage />} />
      </Route>

      {/* Catch all - redirect to dashboard */}
      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  );
};

export default AppRoutes;
